'use client';

import { md5 } from '@noble/hashes/legacy.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

const CONTROL_PREFIX = '/api/browser-upload-sessions/';
const TOKEN_HEADER = 'x-imai-upload-session-token';

export type BrowserUploadControl = { endpoint: string; token: string };

export function parseBrowserUploadControl(
  uploadUrl: string
): BrowserUploadControl | null {
  if (!uploadUrl.startsWith(CONTROL_PREFIX)) return null;
  const hashIndex = uploadUrl.indexOf('#');
  if (hashIndex < 0 || uploadUrl.indexOf('?', 0) >= 0) return null;
  const endpoint = uploadUrl.slice(0, hashIndex);
  const token = uploadUrl.slice(hashIndex + 1);
  const sessionId = endpoint.slice(CONTROL_PREFIX.length);
  if (!/^[a-z0-9]+$/i.test(sessionId) || !token || token.includes('#'))
    return null;
  return { endpoint, token };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export async function hashBrowserUploadBody(body: Blob | File) {
  const sha = sha256.create();
  const md = md5.create();
  const reader = body.stream().getReader();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    sha.update(value);
    md.update(value);
  }
  return {
    contentHash: `sha256:${bytesToHex(sha.digest())}`,
    contentMd5: base64(md.digest()),
    sizeBytes: body.size,
  };
}

export async function postBrowserUploadControl<T>(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal: AbortSignal
): Promise<T> {
  const response = await fetch(control.endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      [TOKEN_HEADER]: control.token,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Upload session failed (${response.status}): ${detail}`);
  }
  return (await response.json()) as T;
}
